import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import Registration from '@components/Templates/Registration';
import { getCompanyDetailsByUrl } from '@api/company';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { LoadingBackdrop } from '@shared/molecules';
import { Parser } from 'html-to-react';
import { PublicCompanyDetails, castResponse } from 'custom.d';

const RegistrationPage: React.FunctionComponent = () => {
  const { companyURL } = useParams<UrlParamsType>();
  const [companyDetails, setCompanyDetails] = useState<PublicCompanyDetails | null>(null);
  const [isCompanyConfigLoading, setIsCompanyConfigLoading] = useState(() => Boolean(companyURL));
  let content = (
    <>
      <p>Unlock the power of Happiness with Hapstar</p>
    </>
  );
  let organizationLogo;
  let organizationAltText;
  let organizationId;
  let hideHapstarLogo = false;
  let platformName: string | null = null;

  // Domain-specific redirect: bare /signup -> /signup/ on Beacon only
  useEffect(() => {
    const { hostname, pathname } = window.location;

    if (hostname === 'beacon.laurahydefoundation.org' && pathname === '/signup') {
      window.location.replace('https://beacon.laurahydefoundation.org/signup/');
    }
  }, []);

  useEffect(() => {
    if (!companyURL) {
      setCompanyDetails(null);
      setIsCompanyConfigLoading(false);

      return undefined;
    }

    let cancelled = false;
    setIsCompanyConfigLoading(true);

    const getCompanyData = async () => {
      try {
        const response = await getCompanyDetailsByUrl(companyURL);
        if (cancelled) {
          return;
        }

        if (response?.success) {
          const data = castResponse<PublicCompanyDetails>(response);
          setCompanyDetails(data);
        } else {
          setCompanyDetails(null);
        }
      } catch {
        if (!cancelled) {
          setCompanyDetails(null);
        }
      } finally {
        if (!cancelled) {
          setIsCompanyConfigLoading(false);
        }
      }
    };

    getCompanyData();

    return () => {
      cancelled = true;
    };
  }, [companyURL]);

  if (isCompanyConfigLoading) {
    return (
      <AuthenticationLayout title="SignUp">
        <LoadingBackdrop text="Loading..." />
      </AuthenticationLayout>
    );
  }

  if (companyDetails !== null) {
    content = Parser().parse(companyDetails.signUpText);
    organizationLogo = companyDetails.companyLogo
      ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.companyLogo}`
      : undefined;
    organizationAltText = `${companyDetails.companyName} logo`;
    organizationId = companyDetails.id;
    hideHapstarLogo = companyDetails.hideHapstarLogo;
    platformName =
      companyDetails.platformName &&
      companyDetails.platformName !== null &&
      companyDetails.platformName !== 'null'
        ? companyDetails.platformName
        : null;
  }

  return (
    <Registration
      content={content}
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      organizationId={organizationId}
      companyURL={companyURL}
      hideHapstarLogo={hideHapstarLogo}
      platformName={platformName}
    />
  );
};

type UrlParamsType = {
  companyURL: string;
};

export default RegistrationPage;
